import CookiesServices from "../services/CookiesServices";

export const ROLE_SUPER_ADMIN = 1;
export const ROLE_VENDOR_USER = 3;

const VENDOR_KEYS = ["VendorId", "vendorId", "vendorid"];

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Same semantics as AuthServices role candidates — API/JWT field names vary by endpoint. */
function normalizeRoleToNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object" && value !== null) {
    if ("id" in value) return normalizeRoleToNumber(value.id);
    if ("roleid" in value) return normalizeRoleToNumber(value.roleid);
    if ("roleId" in value) return normalizeRoleToNumber(value.roleId);
  }
  return null;
}

/**
 * Single source of truth for numeric role id (must match AuthServices.getCurrentRoleId intent).
 * Previously getAccessContext used a narrow key list and missed `role`, nested `role`, etc., which
 * could leave roleId wrong or let JWT/cookie disagree — vendor scoping then stripped all data.
 */
export function getNumericRoleIdFromSession() {
  const user = parseCookieUser();
  const rawUser = CookiesServices.get("user");
  const tokenPayload = parseJwtPayload(CookiesServices.get("token"));

  const candidates = [];
  if (user) {
    candidates.push(
      user.UserRoleId,
      user.userRoleId,
      user.userroleid,
      user.roleId,
      user.roleid,
      user.role_id,
      user.role,
      user.role?.id,
      user.role?.roleid,
      user.role?.roleId,
      user.role?.role_id
    );
  }
  if (tokenPayload) {
    candidates.push(
      tokenPayload.UserRoleId,
      tokenPayload.userRoleId,
      tokenPayload.userroleid,
      tokenPayload.roleId,
      tokenPayload.roleid,
      tokenPayload.role_id,
      tokenPayload.role,
      tokenPayload.Role
    );
  }

  for (const c of candidates) {
    const n = normalizeRoleToNumber(c);
    if (n !== null) return n;
  }

  if (typeof rawUser === "string") {
    try {
      const decoded = rawUser.includes("%") ? decodeURIComponent(rawUser) : rawUser;
      const roleMatch = decoded.match(
        /"(?:userroleid|roleid|roleId|role_id)"\s*:\s*(\d+)/
      );
      if (roleMatch && roleMatch[1] !== undefined) {
        return Number(roleMatch[1]);
      }
    } catch {
      // ignore
    }
  }

  return null;
}

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const readFirstNumeric = (obj, keys) => {
  if (!isObject(obj)) return null;
  for (const key of keys) {
    const found = toNumberOrNull(obj[key]);
    if (found !== null) return found;
  }
  return null;
};

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadB64.padEnd(
      payloadB64.length + ((4 - (payloadB64.length % 4)) % 4),
      "="
    );
    const payloadJson = atob(padded);
    return JSON.parse(payloadJson);
  } catch {
    return null;
  }
};

const parseCookieUser = () => {
  const raw = CookiesServices.get("user");
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    try {
      const decoded = raw.includes("%") ? decodeURIComponent(raw) : raw;
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }
};

const collectVendorIds = (input, ids = []) => {
  if (Array.isArray(input)) {
    input.forEach((item) => collectVendorIds(item, ids));
    return ids;
  }
  if (!isObject(input)) return ids;

  for (const [key, value] of Object.entries(input)) {
    if (VENDOR_KEYS.includes(key)) {
      const normalized = toNumberOrNull(value);
      if (normalized !== null) ids.push(normalized);
    } else if (isObject(value) || Array.isArray(value)) {
      collectVendorIds(value, ids);
    }
  }
  return ids;
};

const setVendorIdOnPayload = (payload, vendorId) => {
  if (!isObject(payload) || vendorId === null) return payload;

  const hasVendorKey = VENDOR_KEYS.some((key) => Object.prototype.hasOwnProperty.call(payload, key));
  if (!hasVendorKey) {
    payload.vendorId = vendorId;
  } else {
    VENDOR_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        payload[key] = vendorId;
      }
    });
  }

  if (isObject(payload.filters)) {
    const hasFilterVendor = VENDOR_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(payload.filters, key)
    );
    if (!hasFilterVendor) {
      payload.filters.vendorId = vendorId;
    } else {
      VENDOR_KEYS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(payload.filters, key)) {
          payload.filters[key] = vendorId;
        }
      });
    }
  }
  return payload;
};

const itemBelongsToVendor = (item, vendorId) => {
  if (!isObject(item) || vendorId === null) return true;
  const itemVendorId = readFirstNumeric(item, VENDOR_KEYS);
  if (itemVendorId === null) return true;
  return itemVendorId === vendorId;
};

const filterAnyShapeByVendor = (value, vendorId) => {
  if (Array.isArray(value)) {
    return value.filter((item) => itemBelongsToVendor(item, vendorId));
  }
  if (!isObject(value)) return value;

  const itemVendorId = readFirstNumeric(value, VENDOR_KEYS);
  if (itemVendorId !== null && itemVendorId !== vendorId) {
    return {};
  }

  const next = { ...value };
  if (Array.isArray(next.result)) next.result = filterAnyShapeByVendor(next.result, vendorId);
  if (Array.isArray(next.data)) next.data = filterAnyShapeByVendor(next.data, vendorId);
  if (Array.isArray(next.rows)) next.rows = filterAnyShapeByVendor(next.rows, vendorId);
  if (Array.isArray(next.items)) next.items = filterAnyShapeByVendor(next.items, vendorId);
  return next;
};

export const getAccessContext = () => {
  const user = parseCookieUser();
  const tokenPayload = parseJwtPayload(CookiesServices.get("token"));

  const roleId = getNumericRoleIdFromSession();
  const vendorId =
    readFirstNumeric(user, VENDOR_KEYS) ??
    readFirstNumeric(tokenPayload, VENDOR_KEYS) ??
    null;

  return {
    roleId,
    vendorId,
    isSuperAdmin: roleId === ROLE_SUPER_ADMIN,
    isVendorUser: roleId === ROLE_VENDOR_USER,
  };
};

export const applyVendorScopeToRequest = (config) => {
  const ctx = getAccessContext();
  if (!ctx.isVendorUser) return config;

  if (ctx.vendorId === null) {
    throw new Error("Access denied: vendor user does not have VendorId");
  }

  const method = String(config?.method || "get").toLowerCase();
  if (!config.headers) config.headers = {};
  config.headers["x-user-role-id"] = String(ctx.roleId);
  config.headers["x-vendor-id"] = String(ctx.vendorId);

  if (["post", "put", "patch"].includes(method)) {
    if (isObject(config.data)) {
      const submittedIds = collectVendorIds(config.data);
      if (submittedIds.some((id) => id !== ctx.vendorId)) {
        throw new Error("Access denied: cross-vendor payload is not allowed");
      }
      config.data = setVendorIdOnPayload({ ...config.data }, ctx.vendorId);
    }
  } else if (method === "delete") {
    if (isObject(config.data)) {
      const submittedIds = collectVendorIds(config.data);
      if (submittedIds.some((id) => id !== ctx.vendorId)) {
        throw new Error("Access denied: cross-vendor payload is not allowed");
      }
      config.data = setVendorIdOnPayload({ ...config.data }, ctx.vendorId);
    }
  } else {
    const params = isObject(config.params) ? { ...config.params } : {};
    const submittedIds = collectVendorIds(params);
    if (submittedIds.some((id) => id !== ctx.vendorId)) {
      throw new Error("Access denied: cross-vendor query is not allowed");
    }
    config.params = setVendorIdOnPayload(params, ctx.vendorId);
  }

  return config;
};

export const applyVendorScopeToResponse = (responseData) => {
  const ctx = getAccessContext();
  // Only vendor role (3) may be scoped; super admin / others must see full payloads.
  if (
    ctx.roleId === ROLE_SUPER_ADMIN ||
    !ctx.isVendorUser ||
    ctx.vendorId === null
  ) {
    return responseData;
  }
  return filterAnyShapeByVendor(responseData, ctx.vendorId);
};
